import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { RECORDING_STATUS } from "../../../utils/constants";
import { MediaCaptureContext } from "../../context/MediaCaptureContext";
import "./MediaComponent.css";
import { ReactComponent as Checkmark } from "../../../assets/media_icons/checkmark.svg";
import { ReactComponent as Reverse } from "../../../assets/media_icons/reverse.svg";
import MediaPlayer from "../MediaPlater/MediaPlayer";
import MediaButton from "../MediaButton/MediaButton";
import { calculateTime } from "../../../utils/mediaUtils";
import { useMediaRecorder } from "./useMediaRecorder";
import icoCheck from '../../../assets/ico-check.svg';
import { ReactComponent as VideoRecord } from "../../../assets/media_icons/video-record.svg";

import popSound from "../../../assets/audios/pop.mp3";
import { useTimeLimitRecorder } from "./useTimeLimitRecorder";

export const VideoRecorderComponent = ({
  timer = 0,
  submitDisabled = false,
  onConfirmVideo = () => { },
  isSendData = false,
  recordingLimit = 0, // recording limit (in seconds), 0 means unlimited 
}) => {
  const {
    videoRef,
    recordingStatus,
    mediaUrl,
    mediaBlob,
    startRecording,
    stopRecording,
    discardRecording,
    switchCamera,
  } = useContext(MediaCaptureContext);
  const timeoutLimitRef = useRef(null);

  const videoSourceRef = useRef();
  const {
    isPlaying,
    time,
    setTime,
    seekProgress,
    maxValue,
    setMaxValue,
    handleMediaTimeUpdated,
    handleStartRecording,
    handleTogglePause,
    handleDiscardMedia,
    handleSeek,
    handleEnded,
  } = useMediaRecorder({
    mediaRef: videoSourceRef,
    mediaUrl,
    startRecording,
    discardRecording,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const playSound = () => {
    const sound = new Audio(popSound);
    sound.play();
  };

  useEffect(() => {
    if (!videoSourceRef.current) return;
    const cacheVideoRef = videoSourceRef.current;

    const handleLoadedMetadata = () => {
      if (videoSourceRef.current.readyState > 0) {
        // FIXME: Firefox displays Infinity:NaN
        setMaxValue(Math.round(videoSourceRef.current.duration * 10) / 10);
        setTime(calculateTime(videoSourceRef.current.duration));
      }
    }

    cacheVideoRef.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => {
      if (cacheVideoRef) {
        cacheVideoRef.removeEventListener('loadedmetadata', handleLoadedMetadata);
      }
      if (timeoutLimitRef.current) {
        clearTimeout(timeoutLimitRef.current);
      }
    }
  }, [setTime, setMaxValue])

  const handleConfirmDecision = useCallback(() => {
    if (!mediaUrlRef.current) return;
    cleanTimeout();

    playSound();
    setIsSubmitting(true); // Bloqueamos los botones
    onConfirmVideo(mediaUrlRef.current, mediaBlobRef.current);
  }, [onConfirmVideo]);

  const {
    mediaBlobRef,
    mediaUrlRef,
    cleanTimeout,
  } = useTimeLimitRecorder({
    mediaBlob,
    mediaUrl,
    timer,
    onConfirmSubmit: handleConfirmDecision,
  })

  const onStartRecording = () => {
    if (recordingLimit > 0) {
      if (timeoutLimitRef.current) {
        clearTimeout(timeoutLimitRef.current);
      }
      timeoutLimitRef.current = setTimeout(() => {
        onTimeoutStopRecording();
      }, recordingLimit * 1000);
    }
    handleStartRecording();
  }

  const onDiscardMedia = () => {
    if (timeoutLimitRef.current) {
      clearTimeout(timeoutLimitRef.current);
    }
    handleDiscardMedia();
  }

  const onTimeoutStopRecording = () => {
    if (timeoutLimitRef.current) {
      clearTimeout(timeoutLimitRef.current);
    }
    stopRecording();
  }

  return (
    <div className="media-question-container">
      <div className="media-confirmation-container">
        <div className="media-preview-wrapper">
          {/* Preview */}
          <div
            className="media-preview-container"
            style={{ display: !mediaUrl ? "block" : "none" }}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="media-preview-video"
            />
            <div style={{ position: "absolute", bottom: 0, width: "100%", zIndex: 2 }}>
              <div className="flex gap-1 p-2 items-center justify-between">
                {(recordingStatus === RECORDING_STATUS.idle || recordingStatus === RECORDING_STATUS.stopped || !recordingStatus) &&
                  <MediaButton onClick={switchCamera}>
                    <Reverse height={35} width={35} />
                  </MediaButton>}
                {recordingStatus === RECORDING_STATUS.recording &&
                  <MediaButton onClick={onTimeoutStopRecording}>
                    <Checkmark height={35} width={35} />
                  </MediaButton>}
                <MediaButton
                  pressed={recordingStatus === RECORDING_STATUS.recording || submitDisabled}
                  disabled={recordingStatus === RECORDING_STATUS.recording || isSubmitting || submitDisabled}
                  size="lg"
                  variant="red"
                  onClick={onStartRecording}
                >
                  <div className="media-record-thumb-container">
                    <div className={`${recordingStatus === RECORDING_STATUS.recording ? " media-record-thumb-recording" : ""}`}>
                      <VideoRecord height={40} width={40} className="media-icon-outline" />
                    </div>
                  </div>
                </MediaButton>
              </div>
            </div>
          </div>

          <div
            className="media-preview-container"
            style={{ display: mediaUrl ? "block" : "none" }}
          >
            {/* media recorded */}
            <video
              ref={videoSourceRef}
              className="media-preview-video"
              onTimeUpdate={handleMediaTimeUpdated}
              onEnded={handleEnded}
            >
              {mediaUrl && (
                <source src={mediaUrl} />
              )}
            </video>
          </div>
        </div>
        <div className="flex w-full items-center gap-1">
          <MediaPlayer
            time={time}
            displayPlayButton={mediaUrl.length > 0 && !submitDisabled}
            isPlaying={isPlaying}
            onClickPlay={handleTogglePause}
            disabled={isSubmitting || !mediaUrl}
            displayDiscardButton={mediaUrl.length > 0 && !submitDisabled}
            progress={seekProgress}
            maxValue={maxValue}
            onClickDiscard={onDiscardMedia}
            onChangeSeekBar={handleSeek}
          />
        </div>
        <button className="ConfirmButton"
          onClick={handleConfirmDecision}
          disabled={isSubmitting || !mediaUrl || submitDisabled}>
          <div className="principalBtn">
            {isSubmitting ? (isSendData ? "Enviado" : "Enviando...") : "Enviar"}
            {isSendData && <img className="sendCheck" src={icoCheck} alt="player ready" />}
          </div>
        </button>
      </div>

    </div>
  );
};

export default VideoRecorderComponent;
